/**
 * Menu de contexto (clique direito).
 *
 * Fica em `position: fixed` na posição do cursor e se ajusta para não vazar da
 * viewport. Fecha em clique fora, `Escape`, scroll, resize ou perda de foco da
 * janela — qualquer coisa que mova o conteúdo sob o menu, senão ele apontaria
 * para a linha errada.
 */
import { Icon } from '@iconify/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface ItemMenu {
  rotulo: string;
  icone: string;
  onClick: () => void;
  desabilitado?: boolean;
  /** Motivo do bloqueio, exibido como `title` — sem isso o item apenas "não faz nada". */
  motivoBloqueio?: string;
  /** Ação destrutiva: pinta em vermelho. */
  perigo?: boolean;
  separadorAntes?: boolean;
}

/** Folga da borda da janela ao reposicionar o menu. */
const MARGEM = 8;

export function MenuContexto({
  x,
  y,
  titulo,
  itens,
  aoFechar,
}: {
  x: number;
  y: number;
  titulo?: string;
  itens: ItemMenu[];
  aoFechar: () => void;
}) {
  const menu = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ x, y });

  // Mede depois de montar e puxa para dentro da viewport quando estoura.
  useLayoutEffect(() => {
    const el = menu.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      x: Math.min(x, window.innerWidth - width - MARGEM),
      y: Math.min(y, window.innerHeight - height - MARGEM),
    });
  }, [x, y]);

  useEffect(() => {
    const fecharNoClique = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node)) aoFechar();
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        aoFechar();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

      // Navegação por teclado entre os itens habilitados.
      e.preventDefault();
      const botoes = [
        ...(menu.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []),
      ];
      if (botoes.length === 0) return;
      const atual = botoes.indexOf(document.activeElement as HTMLButtonElement);
      const passo = e.key === 'ArrowDown' ? 1 : -1;
      const proximo = (atual + passo + botoes.length) % botoes.length;
      botoes[proximo]?.focus();
    };

    document.addEventListener('mousedown', fecharNoClique);
    document.addEventListener('keydown', aoTeclar);
    // `capture` para pegar scroll de qualquer container, não só da janela.
    window.addEventListener('scroll', aoFechar, true);
    window.addEventListener('resize', aoFechar);
    window.addEventListener('blur', aoFechar);

    return () => {
      document.removeEventListener('mousedown', fecharNoClique);
      document.removeEventListener('keydown', aoTeclar);
      window.removeEventListener('scroll', aoFechar, true);
      window.removeEventListener('resize', aoFechar);
      window.removeEventListener('blur', aoFechar);
    };
  }, [aoFechar]);

  // Foca o primeiro item habilitado: permite operar o menu só com o teclado.
  useEffect(() => {
    menu.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }, []);

  return (
    <div
      ref={menu}
      className="sga-menu-ctx"
      role="menu"
      style={{ left: pos.x, top: pos.y }}
      // O menu já é o resultado de um clique direito; outro clique direito
      // dentro dele não deve abrir o menu do navegador.
      onContextMenu={(e) => e.preventDefault()}
    >
      {titulo && <div className="sga-menu-ctx__titulo">{titulo}</div>}

      {itens.map((item) => (
        <div key={item.rotulo} style={{ display: 'contents' }}>
          {item.separadorAntes && <div className="sga-menu-ctx__separador" />}
          <button
            type="button"
            role="menuitem"
            className={`sga-menu-ctx__item${item.perigo ? ' sga-menu-ctx__item--perigo' : ''}`}
            disabled={item.desabilitado}
            title={item.desabilitado ? item.motivoBloqueio : undefined}
            onClick={() => {
              aoFechar();
              item.onClick();
            }}
          >
            <Icon icon={item.icone} width={16} />
            {item.rotulo}
          </button>
        </div>
      ))}
    </div>
  );
}
